import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Zap } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './chartTheme';

function MiniLine({ data, yKey, yLabel, color, decimals = 1 }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={`${yLabel} vs SoC`} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-${yKey}`} />
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={data} margin={{ top: 4, right: 12, left: 4, bottom: 20 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="soc_pct" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'SoC (%)', position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }}
            tickFormatter={(v) => Number(v).toFixed(0)}
          />
          <YAxis tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={(v) => `SoC: ${Number(v).toFixed(1)}%`}
            formatter={(v) => Number(v).toFixed(decimals)}
          />
          <Line type="monotone" dataKey={yKey} stroke={color} {...LINE_PROPS} />
          {zoom.isSelecting && (
            <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Current/Voltage/Power vs SoC (spec §6.2 charts 2-4): the CC-CV curve shape itself —
 * current flat/tapering, voltage rising then flattening, power rising/plateauing then
 * dropping sharply once the taper dominates. Clipped to the user's Start SoC -> Target SoC
 * selection, since that's the session actually being charted.
 */
const ChargingCurveChart = ({ result, startSoc, targetSoc }) => {
  const data = useMemo(() => {
    if (!result) return [];
    const lo = startSoc - 0.05;
    const hi = targetSoc + 0.05;
    return result.series_soc_pct
      .map((soc, i) => ({
        soc_pct: soc,
        current_a: result.series_current_a[i],
        voltage_v: result.series_voltage_v[i],
        power_kw: result.series_power_kw[i],
      }))
      .filter((d) => d.soc_pct >= lo && d.soc_pct <= hi);
  }, [result, startSoc, targetSoc]);

  if (data.length === 0) return null;

  return (
    <ChartCard
      icon={Zap}
      title="Charging Curves vs SoC"
      description="Current, voltage, and power across the selected charge — the CC-CV curve shape itself."
      testId="chart-charging-curves"
    >
      <div className="grid sm:grid-cols-3 gap-4">
        <MiniLine data={data} yKey="current_a" yLabel="Current (A)" color={SERIES_COLORS[0]} decimals={1} />
        <MiniLine data={data} yKey="voltage_v" yLabel="Voltage (V)" color={SERIES_COLORS[2]} decimals={1} />
        <MiniLine data={data} yKey="power_kw" yLabel="Power (kW)" color={SERIES_COLORS[7]} decimals={2} />
      </div>
    </ChartCard>
  );
};

export default ChargingCurveChart;
